import React from 'react';
import { useNavigate } from 'react-router-dom';
import ColoringApp from '../miniapps/ColoringApp';
import TicTacToeApp from '../miniapps/TicTacToeApp';
import MathQuizApp from '../miniapps/MathQuizApp';
import MultipleChoiceEngine from '../miniapps/engines/MultipleChoiceEngine';
import SequenceEngine from '../miniapps/engines/SequenceEngine';
import MatchEngine from '../miniapps/engines/MatchEngine';
import ExplorationCardEngine from '../miniapps/engines/ExplorationCardEngine';
import PatternEngine from '../miniapps/engines/PatternEngine';
import GearDirectionEngine from '../miniapps/engines/GearDirectionEngine';
import GearSpeedEngine from '../miniapps/engines/GearSpeedEngine';
import CircuitPathEngine from '../miniapps/engines/CircuitPathEngine';
import SwitchLogicEngine from '../miniapps/engines/SwitchLogicEngine';
import {
  sampleAnimalHabitatsActivity,
  sampleRobotMovementSequence,
  sampleMathPatternActivity,
  sampleMatchColors,
  sampleExplorationCards,
  sampleGearDirectionTrain,
  sampleGearSpeedFastest,
  sampleCircuitPathRow,
  sampleSwitchLogicAnd,
} from '../miniapps/engines/samplePayloads';

const sectionTitle = {
  color: 'var(--color-text)',
  marginBottom: 12,
  textAlign: 'center',
  fontSize: '1.25rem',
  fontWeight: 800,
};

const grid = {
  maxWidth: 1200,
  margin: '0 auto',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
  gap: 24,
};

const MiniAppDemoPage = () => {
  const navigate = useNavigate();

  return (
    <div style={{
      minHeight: '100vh',
      padding: 20,
      background: 'var(--color-bg)',
    }}
    >
      <button className="btn" style={{ marginBottom: 24 }} onClick={() => navigate('/')}>
        ← Back to Home
      </button>

      <h1 style={{
        color: 'var(--color-text)',
        fontSize: 32,
        fontWeight: 'bold',
        marginBottom: 16,
        textAlign: 'center',
      }}
      >
        Mini-app demo
      </h1>

      <p style={{
        color: 'var(--color-text)',
        textAlign: 'center',
        marginBottom: 20,
        fontSize: 18,
        opacity: 0.9,
        maxWidth: 640,
        marginLeft: 'auto',
        marginRight: 'auto',
        lineHeight: 1.5,
      }}
      >
        Classic apps plus JSON-driven educational engines (no backend). Embed in chat with{' '}
        <code style={{ fontSize: 14 }}>{`{ "type":"miniapp","app":"engine_sequence",... }`}</code>
      </p>

      <h2 style={{ ...sectionTitle, marginTop: 32, fontSize: '1.5rem' }}>Educational engines</h2>
      <p style={{
        textAlign: 'center',
        color: 'var(--color-text)',
        opacity: 0.8,
        marginBottom: 24,
        maxWidth: 720,
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
      >
        Samples: animal habitats (multiple choice), robot movement (sequence), math patterns — plus match and exploration cards.
      </p>

      <div style={grid}>
        <div>
          <h3 style={sectionTitle}>Multiple choice — habitats</h3>
          <MultipleChoiceEngine
            {...sampleAnimalHabitatsActivity}
            activityId="demo_mc_habitats"
            onProgress={(d) => console.debug('engine progress', d)}
            onComplete={(d) => console.debug('engine complete', d)}
          />
        </div>
        <div>
          <h3 style={sectionTitle}>Sequence — robot start</h3>
          <SequenceEngine
            {...sampleRobotMovementSequence}
            activityId="demo_seq_robot"
            onProgress={(d) => console.debug('engine progress', d)}
            onComplete={(d) => console.debug('engine complete', d)}
          />
        </div>
        <div>
          <h3 style={sectionTitle}>Pattern — math</h3>
          <PatternEngine
            {...sampleMathPatternActivity}
            activityId="demo_pattern_math"
            onProgress={(d) => console.debug('engine progress', d)}
            onComplete={(d) => console.debug('engine complete', d)}
          />
        </div>
        <div>
          <h3 style={sectionTitle}>Match — land &amp; water</h3>
          <MatchEngine
            {...sampleMatchColors}
            activityId="demo_match_colors"
            onProgress={(d) => console.debug('engine progress', d)}
            onComplete={(d) => console.debug('engine complete', d)}
          />
        </div>
        <div>
          <h3 style={sectionTitle}>Exploration cards — kelp</h3>
          <ExplorationCardEngine
            {...sampleExplorationCards}
            activityId="demo_explore_kelp"
            onProgress={(d) => console.debug('engine progress', d)}
            onComplete={(d) => console.debug('engine complete', d)}
          />
        </div>
        <div>
          <h3 style={sectionTitle}>Gear direction — mesh train</h3>
          <GearDirectionEngine
            {...sampleGearDirectionTrain}
            activityId="demo_gear_dir"
            onProgress={(d) => console.debug('engine progress', d)}
            onComplete={(d) => console.debug('engine complete', d)}
          />
        </div>
        <div>
          <h3 style={sectionTitle}>Gear speed — fastest pick</h3>
          <GearSpeedEngine
            {...sampleGearSpeedFastest}
            activityId="demo_gear_speed"
            onProgress={(d) => console.debug('engine progress', d)}
            onComplete={(d) => console.debug('engine complete', d)}
          />
        </div>
        <div>
          <h3 style={sectionTitle}>Circuit path — gap row</h3>
          <CircuitPathEngine
            {...sampleCircuitPathRow}
            activityId="demo_circuit_path"
            onProgress={(d) => console.debug('engine progress', d)}
            onComplete={(d) => console.debug('engine complete', d)}
          />
        </div>
        <div>
          <h3 style={sectionTitle}>Switch logic — AND goal</h3>
          <SwitchLogicEngine
            {...sampleSwitchLogicAnd}
            activityId="demo_switch_logic"
            onProgress={(d) => console.debug('engine progress', d)}
            onComplete={(d) => console.debug('engine complete', d)}
          />
        </div>
      </div>

      <h2 style={{ ...sectionTitle, marginTop: 48, fontSize: '1.5rem' }}>Classic mini-apps</h2>
      <div style={grid}>
        <div>
          <h3 style={sectionTitle}>Coloring</h3>
          <ColoringApp
            palette={['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#ffa500', '#800080']}
          />
        </div>
        <div>
          <h3 style={sectionTitle}>Tic tac toe</h3>
          <TicTacToeApp />
        </div>
        <div>
          <h3 style={sectionTitle}>Math quiz</h3>
          <MathQuizApp />
        </div>
      </div>

      <div style={{
        marginTop: 32,
        textAlign: 'center',
        color: 'var(--color-text)',
        opacity: 0.8,
      }}
      >
        <button
          className="btn"
          onClick={() => navigate('/chat')}
          style={{
            backgroundColor: 'var(--color-primary)',
            color: 'var(--color-bg-card)',
            marginTop: 16,
          }}
        >
          Try in chat →
        </button>
      </div>
    </div>
  );
};

export default MiniAppDemoPage;
